<script setup lang="ts">
import { ref } from 'vue';
import { useDisplay } from 'vuetify';
import { useFontResizer } from '../composables/useFontResizer';

const props = defineProps<{
  selected: boolean;
  option: string;
  index: number;
  isCorrectAnswer?: boolean;
  percentage?: number;
  wrongAnswer?: boolean;
}>();

const colors = ['#FF9800', '#2196F3', '#4CAF50', '#9C27B0'];
const icons = ['fa-square', 'fa-circle', 'fa-triangle', 'fa-diamond'];
const cardColor = ref(colors[props.index % colors.length]);
const cardIcon = ref(icons[props.index % icons.length]);

const { smAndDown } = useDisplay();

const optionEl = ref<HTMLElement | null>(null);

const { fontSize, isTruncated } = useFontResizer(optionEl, undefined, 12, 20);
</script>

<template>
  <v-card
    data-testid="quizter-answer-card"
    max-height="150"
    :height="smAndDown ? 110 : 150"
    class="answer-card-container"
    :class="{
      selected: selected,
      'correct-card': isCorrectAnswer,
      'wrong-answer-card': wrongAnswer,
    }"
    color="surface-container-highest"
    :style="{ '--card-accent': cardColor }"
  >
    <div
      class="color-stripe d-flex flex-column justify-center align-center px-3"
      :style="{ backgroundColor: cardColor }"
    >
      <v-icon size="small" color="surface-container-lowest" :icon="`fa:fawsb ${cardIcon}`"></v-icon>
    </div>
    <div
      v-if="percentage || percentage === 0"
      class="percentage-overlay"
      :style="{ '--card-accent': cardColor, '--width': percentage }"
    ></div>
    <div v-if="percentage || percentage === 0" class="percentage-text text-on-surface">{{ percentage }}%</div>
    <div class="card-content pa-2 d-flex align-center justify-center">
      <div ref="optionEl" class="option-box" :class="{ truncate: isTruncated }" :style="{ fontSize: fontSize }">
        <span class="option-text" style="">
          {{ option }}
        </span>
      </div>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
.answer-card-container {
  width: 100%;
  display: flex;
  overflow: hidden;

  position: relative;
  container-type: inline-size; /* enables cqw (container width units) */
  //removes 300ms delay - respond immediately to touch events
  touch-action: manipulation;
}

.color-stripe {
  flex-shrink: 0;
  z-index: 2;
}

.correct-card {
  box-shadow: inset 0 0 0 5px var(--card-accent);
  transform: scale(1.15);
}

.wrong-answer-card {
  opacity: 0.7;
  filter: grayscale(100%);
}

.percentage-overlay {
  background-color: var(--card-accent);
  width: calc(var(--width, 0) * 1%);
  height: 100%;
  opacity: 0.5;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
}

.percentage-text {
  position: absolute;
  right: 0.4rem;
  top: 0.4rem;
  z-index: 1;
}

.card-content {
  width: 100%;
  flex: 1;
  min-width: 0;
  z-index: 1;
}

.option-box {
  max-height: 80%;
  width: 100%;
  min-width: 0;
  text-align: center;
}

.truncate {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
}

.option-text {
  width: 100%;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.selected {
  pointer-events: none;
  user-select: none;
  outline: rgb(var(--v-theme-on-surface)) 4px solid;
}
</style>
